import { RHFTextField } from '@/commons/hook-form';
import { Iconic } from '@/commons/iconic';
import { useCompanyCategory } from '@/hooks/useCompanyCategory';
import { currentFormat } from '@/utils/cases';
import {
  Box,
  Card,
  Stack,
  Button,
  Divider,
  TextField,
  CardHeader,
  Typography,
  CardContent,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

type Props = {
  invoices: Record<string, number>;
  onEdit?: any;
  enableEdit?: boolean;
  exchange?: number;
  onApplyDiscount?: (discount: number) => void;
  enableDiscount?: boolean;
};

const translate: Record<string, string> = {
  tips: 'Propina',
  discount: 'Descuento',
  base: 'Base',
  taxes: 'Impuesto',
};

export default function CheckoutSummary({
  onEdit,
  invoices,
  onApplyDiscount,
  enableEdit = false,
  enableDiscount = false,
  exchange = 0,
}: Props) {
  const [edit, setEdit] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { total, id, tips, ...rest } = invoices;
  const [, setRefund] = useState(false);
  const { setValue, getValues, reset } = useFormContext();
  const { isFoodCompany } = useCompanyCategory();
  const handleValueNumber = (value: string, name: string, maxValue = false) => {
    if (Number(value) > 100 && maxValue) return;
    if (Number(value) < 0) return;
    setValue(name, Number(value));
  };

  useEffect(() => {
    setRefund(true);
  }, [exchange]);

  useEffect(() => {
    reset({ tips: invoices.tips });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  const tipsValue = getValues().tips;
  return (
    <Card sx={{ mb: 3 }}>
      {isFoodCompany && (
        <CardHeader
          title="Resumen de Orden"
          action={
            enableEdit && (
              <>
                {!edit ? (
                  <Button
                    size="small"
                    onClick={() => setEdit((prev) => !prev)}
                    startIcon={<Iconic icon="eva:edit-fill" />}
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        onEdit(tipsValue);
                        setEdit((prev) => !prev);
                      }}
                    >
                      <Iconic icon="material-symbols:check-circle-outline" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setEdit((prev) => !prev);
                        handleValueNumber(tips.toString(), 'tips');
                      }}
                    >
                      <Iconic icon="material-symbols:cancel-outline" />
                    </IconButton>
                  </>
                )}
              </>
            )
          }
        />
      )}
      <CardContent>
        <Stack spacing={2}>
          {Object.entries(rest).map(([key, value]) => (
            <Stack direction="row" justifyContent="space-between" key={key}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {translate[key] || ''}
              </Typography>
              <Typography variant="subtitle2">{value}</Typography>
            </Stack>
          ))}
          {isFoodCompany && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {translate.tips || ''}
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="center">
                <RHFTextField
                  disabled={!edit}
                  variant="standard"
                  name="tips"
                  size="small"
                  defaultValue={invoices.tips}
                  sx={{
                    width: 80,
                  }}
                  placeholder="0.00"
                  onChange={(event) => handleValueNumber(event.target.value, 'tips')}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          $
                        </Box>
                      </InputAdornment>
                    ),
                    type: 'number',
                  }}
                />
              </Stack>
            </Stack>
          )}

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle1">Total</Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle1">{currentFormat(total)}</Typography>
            </Box>
          </Stack>
          {exchange !== total && exchange !== 0 && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1">
                  {exchange > 0 ? 'Devuelta' : 'Faltante'}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="subtitle1" sx={{ color: 'error.main' }}>
                    {currentFormat(Math.abs(exchange))}
                  </Typography>
                </Box>
              </Stack>
            </>
          )}

          {enableDiscount && onApplyDiscount && (
            <TextField
              fullWidth
              placeholder="Discount codes / Gifts"
              value="DISCOUNT5"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={() => onApplyDiscount(5)} sx={{ mr: -0.5 }}>
                      Apply
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
